import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { WorksTitleBar } from './WorksTitleBar';
import { HOME_TOPBAR_H } from '../layout/HomeTopBar';
import { WaterfallView } from './WaterfallView';
import { VideoView } from './VideoView';
import { layoutWaterfall, layoutGrid, GAP, type WorkCard } from './workLayout';
import { API_BASE } from '../../../config';
import { useWorksStore, type PublishedWork } from '../../../stores/worksStore';

/**
 * 作品展示区（设计稿 1-9 #251:32 TAB 行 + 183:57 瀑布流 / 183:179 视频网格）
 * ——即梦式流式布局，无内容缩放
 * - TAB 行：「图像精选」激活态白 /「视频精选」白 45%，18px，间距 24px，与内容区左对齐（y=961）
 * - TAB 行 + 标题栏吸顶：滚动浏览作品时钉在顶栏正下方常驻可见（与页面壳层
 *   HomeTopBar 同款体验）。整页缩放框架拆除后祖先链无 transform/overflow clip，
 *   改回原生 position: sticky（top = HOME_TOPBAR_H）：文档流即吸顶元素；
 *   未与顶栏相接前背景透明（透出页面点阵背景），相接后显示 #121212 遮住身后
 *   滚过的卡片，滚出作品区底部时随内容自然推走
 * - TAB 与内容区间距 24px（TAB 底 979 → 内容顶 1003）；内容列 1400 居中
 * - 瀑布流/视频网格响应式：ResizeObserver 实测内容列宽 → 列数断点
 *   （≥1060 → 4 列 / ≥760 → 3 列 / ≥460 → 2 列 / 其余 1 列），列宽均分剩余宽度，
 *   卡片宽随列宽伸缩、字号恒定；无用户作品且 4 列时保留设计稿手工构图（按列宽等比映射）
 * - 内容首行 32px 标题栏（chips + 搜索），标题栏与卡片区间距 24px（32 + 24 = 卡片起点 y=56）
 * - 「加载更多」：裁剪窗口默认高 1260（底部渐变示意还有内容），点击展开至画布全高并隐藏按钮；两 TAB 独立
 * - 内容数据源：服务端精选接口 /api/works/featured（管理员在 Bong作品页挑选的作品）；
 *   精选池为空或拉取失败时回退设计稿模板卡（WATERFALL_TEMPLATE / VIDEO_SRCS）
 * - 内容归首页：展示管理员精选作品；用户上传的作品全部归入独立页「Bong作品」
 *   （BongWorksPage 复用同款瀑布流/视频网格布局与卡片样式）
 */
type WorksTab = 'image' | 'video';

const TABS: { key: WorksTab; label: string }[] = [
  { key: 'image', label: '图像精选' },
  { key: 'video', label: '视频精选' },
];

/* 设计稿模板卡文案（所有模板卡复用同一套，与设计稿一致） */
const TEMPLATE_TITLE = 'IPhone Air 极简纯净风产品展示';
const VIDEO_TEMPLATE_TITLE = 'iPhone air 极简纯净风产品展示';
const TEMPLATE_USER = '用户7500';
const TEMPLATE_LIKES = '1684';

/* 设计稿原始瀑布流构图（Figma 183:57 #250:4 逐像素反解，13 卡；wf-NN 素材按节点顺序一一对应，
   卡高分布 452×6 / 604×3 / 192×4 与素材比例验证吻合）；列间 12px 等差已全列校验 */
const WATERFALL_TEMPLATE: { key: string; src: string; h: number }[] = [
  { key: 'wf-01', src: '/images/home/waterfall/wf-01.png', h: 452 },
  { key: 'wf-02', src: '/images/home/waterfall/wf-02.png', h: 604 },
  { key: 'wf-03', src: '/images/home/waterfall/wf-03.png', h: 604 },
  { key: 'wf-04', src: '/images/home/waterfall/wf-04.png', h: 452 },
  { key: 'wf-05', src: '/images/home/waterfall/wf-05.png', h: 604 },
  { key: 'wf-06', src: '/images/home/waterfall/wf-06.png', h: 452 },
  { key: 'wf-07', src: '/images/home/waterfall/wf-07.png', h: 452 },
  { key: 'wf-08', src: '/images/home/waterfall/wf-08.png', h: 192 },
  { key: 'wf-09', src: '/images/home/waterfall/wf-09.png', h: 452 },
  { key: 'wf-10', src: '/images/home/waterfall/wf-10.png', h: 192 },
  { key: 'wf-11', src: '/images/home/waterfall/wf-11.png', h: 192 },
  { key: 'wf-12', src: '/images/home/waterfall/wf-12.png', h: 192 },
  { key: 'wf-13', src: '/images/home/waterfall/wf-13.png', h: 452 },
];

/* 设计稿原始坐标（无作品且 4 列时按列宽等比映射还原，x −2 归一列步进；其余情况 layoutWaterfall 重排覆盖） */
const WATERFALL_XY: Record<string, { x: number; y: number }> = {
  'wf-01': { x: 2, y: 0 },
  'wf-02': { x: 2, y: 464 },
  'wf-03': { x: 354, y: 0 },
  'wf-04': { x: 706, y: 464 },
  'wf-05': { x: 1058, y: 0 },
  'wf-06': { x: 354, y: 616 },
  'wf-07': { x: 706, y: 0 },
  'wf-08': { x: 1058, y: 616 },
  'wf-09': { x: 1058, y: 820 },
  'wf-10': { x: 706, y: 928 },
  'wf-11': { x: 354, y: 1080 },
  'wf-12': { x: 2, y: 1080 },
  'wf-13': { x: 706, y: 1132 },
};

/* 设计稿视频网格：20 卡行序（v-20 补位首行空槽，见 VideoView 注释） */
const VIDEO_SRCS = [
  'v-01', 'v-20', 'v-02', 'v-03',
  'v-04', 'v-05', 'v-06', 'v-07',
  'v-08', 'v-09', 'v-10', 'v-11',
  'v-12', 'v-13', 'v-14', 'v-15',
  'v-16', 'v-17', 'v-18', 'v-19',
];

const COL_X = [2, 354, 706, 1058];
const DESIGN_STEP = 352; // 设计稿列步进（340 卡宽 + 12 间距），构图映射用
const VIDEO_CELL_H = 270; // 视频卡 340x270，行步进 282
const VIDEO_ROW_STEP = VIDEO_CELL_H + 12;

/* 封面自然尺寸运行时测量（与 BongWorksPage 同款）：精选作品未存尺寸时补测宽高，
   供瀑布流卡高按真实宽高比换算 */
const useCoverSizes = (sources: string[]) => {
  const [sizes, setSizes] = useState<Record<string, { w: number; h: number }>>({});
  const pendingRef = useRef<Set<string>>(new Set());
  const key = sources.join('\n');
  useLayoutEffect(() => {
    key
      .split('\n')
      .filter(Boolean)
      .forEach((src) => {
        if (pendingRef.current.has(src)) return;
        pendingRef.current.add(src);
        const img = new Image();
        img.onload = () =>
          setSizes((prev) => ({ ...prev, [src]: { w: img.naturalWidth, h: img.naturalHeight } }));
        img.src = src;
      });
  }, [key]);
  return sizes;
};

interface WorksSectionProps {
  /** 点击作品卡 → 预览页（图像 188:19 / 视频 202:501），由 HomePage → AppNew 接管视图切换 */
  onOpen?: (card: WorkCard) => void;
  /** 首页点阵背景的底部边界：绑到「图像精选」TAB 行+标题栏 sticky 容器，
      点阵延伸到本标题栏底部（见 DotMatrixBackground bottomBoundaryRef） */
  worksBoundaryRef?: React.RefObject<HTMLDivElement | null>;
}

const WorksSectionComponent: React.FC<WorksSectionProps> = ({ onOpen, worksBoundaryRef }) => {
  const [tab, setTab] = useState<WorksTab>('image');
  // 「加载更多」展开态（两 TAB 独立）：展开后画布完整显示、按钮隐藏（无更多内容）
  const [imageExpanded, setImageExpanded] = useState(false);
  const [videoExpanded, setVideoExpanded] = useState(false);
  // 标题栏状态提升：文档流哨兵与 portal 吸顶条双实例共享（chip/搜索两处交互同步）
  const [activeChip, setActiveChip] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const handleTab = useCallback((next: WorksTab) => () => setTab(next), []);

  /* —— 服务端精选作品：拉取 /api/works/featured（公开接口），两类型独立；
     null = 未加载 / 空池 / 拉取失败 → 布局回退设计稿模板卡。
     成功后同步写入 worksStore 精选会话态，供点击预览时解析
     （匿名访客 publishedWorks 未 hydrate，须用合并列表查找） —— */
  const [featuredImageWorks, setFeaturedImageWorks] = useState<PublishedWork[] | null>(null);
  const [featuredVideoWorks, setFeaturedVideoWorks] = useState<PublishedWork[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    const fetchType = async (type: 'image' | 'video') => {
      try {
        const res = await fetch(`${API_BASE}/api/works/featured?type=${type}&limit=40`);
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data?.works) ? (data.works as PublishedWork[]) : [];
      } catch {
        return [];
      }
    };
    (async () => {
      const [imgWorks, vidWorks] = await Promise.all([fetchType('image'), fetchType('video')]);
      if (cancelled) return;
      if (imgWorks.length) setFeaturedImageWorks(imgWorks);
      if (vidWorks.length) setFeaturedVideoWorks(vidWorks);
      if (imgWorks.length || vidWorks.length) {
        useWorksStore.getState().setFeaturedWorks([...imgWorks, ...vidWorks]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* —— 即梦式响应式：实测内容列宽 → 列数断点 → 列宽均分剩余宽度 ——
     内容列不再定宽缩放：ResizeObserver 实测容器宽，按断点取列数
     （≥1060 → 4 列 / ≥760 → 3 列 / ≥460 → 2 列 / 其余 1 列），
     列宽 = (容器宽 − 12×(列数−1)) / 列数；卡片随列宽伸缩、字号恒定 */
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentW, setContentW] = useState(1400);
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const update = () => setContentW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* 吸附态检测：吸顶条正前方 1px 文档流哨兵，以「顶栏高度」为观测线（rootMargin 负顶边）。
     哨兵尚未滚过顶栏下缘 = 吸顶条未与顶栏相接 → 背景透明；滚过即相接 → 显示 #121212 底色 */
  const stickySentinelRef = useRef<HTMLDivElement | null>(null);
  const [stuck, setStuck] = useState(false);
  useLayoutEffect(() => {
    const el = stickySentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => setStuck(!entries[0].isIntersecting),
      { rootMargin: `-${HOME_TOPBAR_H}px 0px 0px 0px` },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const cols = contentW >= 1060 ? 4 : contentW >= 760 ? 3 : contentW >= 460 ? 2 : 1;
  const colW = (contentW - GAP * (cols - 1)) / cols;

  /* 图像精选布局：有服务端精选作品 → 瀑布流重排（卡高按封面真实宽高比换算，
     340 宽坐标系限幅 200–680）；无精选 → 模板卡（4 列设计稿构图等比映射，其余断点重排） */
  const featuredImageSrcs = featuredImageWorks?.map((w) => w.coverUrl || w.url) ?? [];
  const featuredCoverSizes = useCoverSizes(featuredImageSrcs);
  const imageLayout = useMemo(() => {
    if (featuredImageWorks && featuredImageWorks.length) {
      const cards: WorkCard[] = featuredImageWorks.map((w) => {
        const src = w.coverUrl || w.url;
        let ratio: number | null = null;
        if (w.coverWidth && w.coverHeight) ratio = w.coverHeight / w.coverWidth;
        else if (featuredCoverSizes[src]) ratio = featuredCoverSizes[src].h / featuredCoverSizes[src].w;
        const h = ratio !== null ? Math.min(680, Math.max(200, Math.round(340 * ratio))) : 452;
        return {
          key: `feat-${w.id}`,
          id: w.id,
          src,
          h,
          title: w.title,
          user: w.authorName,
          avatar: w.authorAvatar,
          likes: String(w.likes),
        };
      });
      const { placed, canvasH } = layoutWaterfall(cards, cols, colW);
      const byKey = new Map(cards.map((c) => [c.key, c]));
      return {
        cards: placed.map((p) => ({ ...(byKey.get(p.key) as WorkCard), x: p.x, y: p.y, w: p.w, h: p.h })),
        canvasH,
      };
    }
    const template: WorkCard[] = WATERFALL_TEMPLATE.map((c) => ({
      ...c,
      id: c.key,
      title: TEMPLATE_TITLE,
      user: TEMPLATE_USER,
      likes: TEMPLATE_LIKES,
    }));
    if (cols === 4) {
      // 4 列：严格还原设计稿手工构图，340 基准坐标按列宽等比映射
      // （x 以设计稿列步进 352 归一 → 实际列步进 colW+GAP，右缘恰好对齐容器）
      const ratio = colW / 340;
      return {
        cards: template.map((c) => ({
          ...c,
          x: (WATERFALL_XY[c.key].x - 2) * ((colW + GAP) / DESIGN_STEP),
          y: WATERFALL_XY[c.key].y * ratio,
          w: colW,
          h: Math.round(c.h * ratio),
        })),
        canvasH: Math.round(1584 * ratio),
      };
    }
    const { placed, canvasH } = layoutWaterfall(template, cols, colW);
    const byKey = new Map(template.map((c) => [c.key, c]));
    return {
      cards: placed.map((p) => ({ ...(byKey.get(p.key) as WorkCard), x: p.x, y: p.y, w: p.w, h: p.h })),
      canvasH,
    };
  }, [cols, colW, featuredImageWorks, featuredCoverSizes]);

  /* 视频精选布局：有服务端精选作品 → 行序网格重排（cellH = 封面 192×colW/340 + 78 文字区）；
     无精选 → 模板卡（4 列设计稿行序构图等比映射，其余断点重排） */
  const videoLayout = useMemo(() => {
    if (featuredVideoWorks && featuredVideoWorks.length) {
      const cards: WorkCard[] = featuredVideoWorks.map((w) => ({
        key: `feat-${w.id}`,
        id: w.id,
        src: w.coverUrl || '',
        h: VIDEO_CELL_H,
        title: w.title,
        user: w.authorName,
        avatar: w.authorAvatar,
        likes: String(w.likes),
        videoUrl: w.url,
        showFlow: w.isPublicWorkflow,
      }));
      const cellH = Math.round((192 * colW) / 340) + 78;
      const { placed, canvasH } = layoutGrid(cards.length, cols, colW, cellH);
      return {
        cards: cards.map((c, i) => ({ ...c, x: placed[i].x, y: placed[i].y, w: placed[i].w, h: placed[i].h })),
        canvasH,
      };
    }
    const template: WorkCard[] = VIDEO_SRCS.map((src) => ({
      key: src,
      id: src,
      src: `/images/home/videos/${src}.png`,
      h: VIDEO_CELL_H,
      title: VIDEO_TEMPLATE_TITLE,
      user: TEMPLATE_USER,
      likes: TEMPLATE_LIKES,
    }));
    if (cols === 4) {
      // 4 列：严格还原设计稿行序构图，坐标按列宽等比映射（x 同瀑布流归一列步进）
      const ratio = colW / 340;
      return {
        cards: template.map((c, i) => ({
          ...c,
          x: (COL_X[i % 4] - 2) * ((colW + GAP) / DESIGN_STEP),
          y: Math.floor(i / 4) * VIDEO_ROW_STEP * ratio,
          w: colW,
          h: Math.round(VIDEO_CELL_H * ratio),
        })),
        canvasH: Math.round(1398 * ratio),
      };
    }
    const cellH = Math.round((192 * colW) / 340) + 78;
    const { placed, canvasH } = layoutGrid(template.length, cols, colW, cellH);
    return {
      cards: template.map((c, i) => ({ ...c, x: placed[i].x, y: placed[i].y, w: placed[i].w, h: placed[i].h })),
      canvasH,
    };
  }, [cols, colW, featuredVideoWorks]);

  /* TAB 行 + 标题栏：吸顶组与文档流同一实例（原生 sticky，无需双实例同步） */
  const tabBar = (
    <>
      <div className="flex h-[18px] items-center gap-6">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={handleTab(key)}
            className="cursor-pointer text-[18px] leading-[18px] text-white transition-opacity"
            style={{ opacity: tab === key ? 1 : 0.45 }}
          >
            {label}
          </button>
        ))}
      </div>
      {/* 标题栏 32px（chips + 搜索），两 TAB 复用同一实例仅切换图标规格 */}
      <div className="mt-6">
        <WorksTitleBar
          variant={tab === 'image' ? 'waterfall' : 'video'}
          activeChip={activeChip}
          onChipChange={setActiveChip}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        />
      </div>
    </>
  );

  return (
    <section className="w-full pt-12">
      <div ref={contentRef} className="mx-auto w-full max-w-[1400px]">
        {/* TAB 行 + 标题栏：原生 sticky 吸顶（top = 顶栏高度）——整页缩放框架拆除后
            祖先链无 transform/overflow clip，文档流即吸顶；哨兵未滚过顶栏下缘前背景
            透明，与顶栏相接后显示 #121212 遮住身后滚过的卡片，滚出
            作品区底部随内容自然推走。pb-3 与下方 mt-3 合计 24px，
            保持设计稿「标题栏 → 卡片」间距。
            同时是首页点阵背景的底部边界（worksBoundaryRef） */}
        <div ref={stickySentinelRef} className="h-px" />
        <div
          ref={worksBoundaryRef}
          className={`sticky z-20 pt-3 pb-3 ${stuck ? 'bg-[#121212]' : 'bg-transparent'}`}
          style={{ top: HOME_TOPBAR_H }}
        >
          {tabBar}
        </div>

        {/* 卡片区：24px 间距 */}
        <div className="mt-3">
          {tab === 'image' ? (
            <div>
              {/* 裁剪窗口：默认高 1260（设计稿 frame 1316 - 标题栏 32 - 间距 24，行五被裁；
                  画布不足 1260 时贴合画布高），点击「加载更多」展开至画布全高 */}
              <div
                className="overflow-hidden"
                style={{
                  height: imageExpanded ? imageLayout.canvasH : Math.min(1260, imageLayout.canvasH),
                  transition: 'height 300ms ease',
                }}
              >
                <WaterfallView
                  expanded={imageExpanded}
                  cards={imageLayout.cards}
                  canvasH={imageLayout.canvasH}
                  onOpen={onOpen}
                />
              </div>
              {/* 「加载更多」：设计稿按钮中心 y=2396（窗口底 2319 + 77），高 30 → 上边距 62；居中胶囊 ≈80x30 浅描边白字 14px；
                  按钮底 2411 → 分隔线 2517 = 106px；展开后无更多内容，按钮隐藏 */}
              {!imageExpanded && (
                <div className="mt-[62px] flex justify-center">
                  <button
                    type="button"
                    onClick={() => setImageExpanded(true)}
                    className="h-[30px] cursor-pointer rounded-full border border-[#3D3D3D] px-3 text-[14px] leading-[14px] text-white transition-colors hover:border-[#5A5A5A] hover:bg-white/5"
                  >
                    加载更多
                  </button>
                </div>
              )}
              <div className="mb-[106px]" />
            </div>
          ) : (
            <div>
              {/* 裁剪窗口：与图像作品同构，默认 1260（行五被裁 + 渐变示意；画布不足时贴合），
                  点击「加载更多」展开至画布全高 */}
              <div
                className="overflow-hidden"
                style={{
                  height: videoExpanded ? videoLayout.canvasH : Math.min(1260, videoLayout.canvasH),
                  transition: 'height 300ms ease',
                }}
              >
                <VideoView
                  expanded={videoExpanded}
                  cards={videoLayout.cards}
                  canvasH={videoLayout.canvasH}
                  onOpen={onOpen}
                />
              </div>
              {!videoExpanded && (
                <div className="mt-[62px] flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVideoExpanded(true)}
                    className="h-[30px] cursor-pointer rounded-full border border-[#3D3D3D] px-3 text-[14px] leading-[14px] text-white transition-colors hover:border-[#5A5A5A] hover:bg-white/5"
                  >
                    加载更多
                  </button>
                </div>
              )}
              <div className="mb-[106px]" />
            </div>
          )}
        </div>

      </div>
    </section>
  );
};

export const WorksSection = memo(WorksSectionComponent);
